<template>
  <GenericDialogLayout
    :data-qa="didMount ? 'did-mount' : ''"
    :loading="isSubmitting"
    :header-text="$t('event.action.new.title')"
    :submit-button-text="$t('event.action.new.create_event')"
    :submit-button-class="'vcita-medium-button'"
    :cancel-button-text="$t('booking.actions.cancel')"
    @submit-dialog="handleSubmit"
    @close-dialog="handleClose"
    @loader-time-out="handleLoaderTimeOut"
    class="event-dialog-container"
  >
    <template v-slot:left-side-pane>
      <v-form class="dialog-form" ref="eventForm">
        <v-layout class="left-side-container" column>

          <v-layout class="event-details-section" column>
            <v-layout class="input-section-title service-input-title" justify-start align-center>
              {{ $t('booking.info.service') }}
            </v-layout>
            <ServicesListSelect
              class="service-select"
              data-qa="service-select-input"
              :categories-data="modifiedCategories"
              :rules="serviceRules"
              v-model="selectedService"
              @change="deleteCopy"
            />
            <v-layout v-if="!$vuetify.breakpoint.mdAndUp" class="dialog-separator service-separator"></v-layout>
            <v-layout class="input-section-title" justify-space-between align-center>
              <span class="event-details-title">{{ $t('event.action.new.event_details') }} </span>
              <RecurrenceSelector @formatted-input="val => formattedRecurrenceParams = val"
                                  left
                                  exclude-never
                                  :start_time="startTimeDateObject" v-model="recurrenceParams"
                                  :duration="getDuration()"
                                  v-if="$vuetify.breakpoint.mdAndUp" class="recurring-link"/>
            </v-layout>


            <v-layout
              class="input-section"
              column
            >
              <vcita-text-field
                :rules="customTitleRules"
                class="custom-title-input"
                v-if="isSelectedServiceCustom"
                maxlength="80"
                :label="$t('event.action.new.event_title')"
                v-model="customTitle"
              />
              <v-layout class="spacing-gap">
                <DatePicker
                  v-model="startDate"
                  :label="$t('booking.info.start_date')"
                  class="one-flex-grow"
                  data-qa="service-date-input"
                  :rules="dateRules"
                >
                </DatePicker>
                <v-layout class="one-flex-grow">
                  <HourPicker
                    name="start_time"
                    :rules="startTimeRules"
                    :label="$t('booking.info.start_at')"
                    data-qa="service-start-time-input"
                    :error-messages="serverErrors.start_time || []"
                    :amPmMode="amPmMode"
                    v-model="startTime"
                  >
                  </HourPicker>
                </v-layout>
              </v-layout>
              <v-layout class="spacing-gap">
                <DatePicker
                  v-model="endDate"
                  :label="$t('booking.info.end_date')"
                  class="one-flex-grow"
                  data-qa="service-date-input"
                  :not-before="startDate"
                  :rules="dateRules"
                  @change="handleEndDate"
                >

                </DatePicker>
                <v-layout class="one-flex-grow">
                  <HourPicker
                    data-qa="service-end-time-input"
                    :error-messages="serverErrors.end_time || []"
                    :label="$t('booking.info.end_at')"
                    name="end_time"
                    :rules="endTimeRules"
                    :amPmMode="amPmMode"
                    v-model="endTime"
                  >
                  </HourPicker>
                </v-layout>
              </v-layout>
              <v-layout class="warning-item" align-content-start
                        v-if="shouldDisplayTimeInPastWarning">
                <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
                <div class="warning-text">{{ $t('common.errors.time_in_past') }}</div>
              </v-layout>

              <v-layout class="warning-item" align-content-start
                        v-if="shouldDisplayEndFollowingDayWarning">
                <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
                <div class="warning-text">{{ $t('settings.service_page.availability.errors.ends_following_day') }}</div>
              </v-layout>
              <v-layout
                class="spacing-gap"
                :column="!$vuetify.breakpoint.mdAndUp"
              >
                <VcitaSelectDropDown
                  :label="$t('booking.info.preparation_time')"
                  v-model="newMeeting.padding"
                  class="one-flex-grow"
                  :items="preparationTimes">
                </VcitaSelectDropDown>
                <VcitaTextField
                  :label="$t('booking.info.max_attendance')"
                  v-model="newMeeting.max_attendance"
                  class="wide one-flex-grow"
                  :rules="maxAttendanceRules"
                  min=1
                  step=1
                  type="number"
                />
              </v-layout>
              <StaffSelection
                v-model="selectedStaff"
                v-if="staffs.length > 1 && shouldDisplayStaffSelect"
                hide-multi-staff
                :staffs="staffs"
                :currentStaffUid="account.staff.uid"
                :rules="[val => !!val]"
              />
              <v-layout class="warning-item" align-content-start
                        v-if="shouldDisplayPrimeStaffDoubleBookingWarning">
                <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
                <div class="warning-text">{{ $t('booking.validation_errors.scheduling_conflict') }}</div>
              </v-layout>

            </v-layout>

          </v-layout>
          <RecurrenceSelector @formatted-input="val => formattedRecurrenceParams = val"
                              exclude-never
                              :start_time="startTimeDateObject" v-model="recurrenceParams"
                              :duration="getDuration()"
                              v-if="!$vuetify.breakpoint.mdAndUp"/>
          <ExpansionPanels
            ref="interactionDetailsPanel"
            class="dialog-expansion-panel"
            @click-expansion="handleExpansionClick"
            :disabled="!selectedService"
            :items="[{
                  name: $t('appointment.info.location_and_price')
                }]"
          >
            <template slot="content" slot-scope="contentSlot">
              <v-layout column class="input-section">

                <LocationSelector
                  :rules="locationRules"
                  class="location-selector"
                  :excludes="locationExcludes"
                  @input="handleLocationSelect"
                  :value="newMeeting.interaction_type"
                  ref="locationSelector"
                />

                <div v-if="shouldDisplayZoomWarning">
                  <VcitaAlert v-if="isSelectedStaffAccountStaff"
                              :type="'attention'"
                              :header-text="'booking.info.interaction_types.zoom.authorize_to_host_title'"
                              :body-text="'booking.info.interaction_types.zoom.taken_to_zoom'"
                              :button-function="handleZoomIntegration"
                              :button-text="'booking.info.interaction_types.zoom.authorize_to_host_connect'"
                  />
                  <VcitaAlert v-if="!isSelectedStaffAccountStaff"
                              :type="'attention'"
                              :header-text="'booking.info.interaction_types.zoom.staff_not_connected_title'"
                              :body-text="$t('booking.info.interaction_types.zoom.staff_needs_to_connect', {staff_name: selectedStaff.full_name})"
                              :link="'https://support.vcita.com/hc/en-us/articles/360045483013#slide-3'"
                              :link-text="'booking.info.interaction_types.zoom.here_how'"
                  />
                </div>
                <InteractionDetailsInput
                  v-if="newMeeting.interaction_type && newMeeting.interaction_type !== 'zoom'"
                  :interactionType="newMeeting.interaction_type"
                  v-model="newMeeting.interaction_details"
                  class="interaction-details-input"
                  :rules="interactionDetailsRules"
                  :defaultCountry="account.business.country_alpha2"
                />
                <FeeTypeGenerator
                  :charge_type="newMeeting.charge_type"
                  :price="newMeeting.price"
                  @price-input="val => newMeeting.price = val"
                  @charge-type-input="val => newMeeting.charge_type = val"
                  :currencySymbol="newMeetingCurrencySymbol"
                  :priceRules="priceRules"
                  :entityType="'event'"
                >

                </FeeTypeGenerator>
              </v-layout>

            </template>

          </ExpansionPanels>
          <v-layout>
            <VcitaCheckbox
              :ripple="false"
              :readonly="!$hasFeature('online_scheduling') || isSelectedServiceCustom"
              :color="getDisabled"
              class="dialog-checkbox"
              v-model="newMeeting.invitation_only"
            >
              <template v-slot:label>
                <v-tooltip
                  top
                  content-class="tooltip"
                  :disabled="$hasFeature('online_scheduling') && !isSelectedServiceCustom">
                  <template v-slot:activator="{ on, attrs }">
                      <span
                        v-on="on"
                        v-bind="attrs">
                        {{ $t('event.action.new.invitation_only') }}
                      </span>
                  </template>
                  {{ $t(!$hasFeature('online_scheduling') ? 'booking.validation_errors.ff_not_active' : 'event.action.new.invitation_only_tooltip') }}
                </v-tooltip>
              </template>
            </VcitaCheckbox>
            <VcitaInfo
              :header="$t('event.action.new.invitation_only')"
              :body="$t('event.action.new.invitation_only_info')"
              :nudge-left="22"
              top
            >
            </VcitaInfo>
          </v-layout>
        </v-layout>
      </v-form>
    </template>
  </GenericDialogLayout>
</template>

<script>
import {bookingMixin} from '../bookingMixin.js'
import {VcitaSelectDropDown, VcitaInfo} from "@/modules/common/components"
import {
  createDateAsUTC,
  convertTime24To12,
} from "@/modules/common/services/timeService.js"
import {eventsService} from "@/modules/events/services/eventsService.js"
import {createEvent} from "@/modules/bookings/services/bookingsService.js"
import {showSuccess,} from "@/modules/common/services/frontageToastService.js"
import moment from "moment";

export default {
  name: 'eventDialog',
  mixins: [bookingMixin],
  components: {
    VcitaSelectDropDown,
    VcitaInfo
  },
  data() {
    return {
      customTitle: '',
      customMaxAttendance: 5,
      locationExcludes: ['client_location', 'client_phone']
    }
  },
  watch: {
    categories: {
      async handler() {
        this.checkServicesMap()
        this.initializeSelectedService()
      }
    }
  },
  async created() {
    this.$track('New Event', Object.assign({}, {new_dialog: true}))
    const {startDate, endDate, startTime, endTime} = this.getPresetTimes()
    this.startDate = startDate
    this.endDate = endDate
    this.startTime = startTime
    this.endTime = endTime

    this.setSelectedStaff()

    let pastIsoDate = new Date()
    pastIsoDate.setMonth(pastIsoDate.getMonth() - 3)
    try {
      const res = await eventsService.get(
        1,
        20,
        "",
        [],
        [(this.selectedStaff && this.selectedStaff.uid) || this.staff.uid],
        pastIsoDate.toISOString(),
        null,
        "desc"
      );

      this.recentlyPickedServices = _.uniqBy(res.events, 'event_service_uid').filter(service => !!service.id).slice(0, 3)
    } catch (e) {
      console.error(e)
    }
  },
  mounted() {
    if (!this.staffUid || !this.selectedStaff) {
      this.selectedStaff = this.staff
    }
    this.$nextTick(() => {
      this.didMount = true
    })
  },
  methods: {
    handleExpansionClick() {
      this.$refs.interactionDetailsPanel.$el.scrollIntoView()
      this.$track('New event - Expand location & Fee')
    },
    updateNewMeetingWithServiceDefaults() {
      const selectedService = this.selectedService

      if (!selectedService) {
        return;
      }
      this.newMeeting.interaction_type = selectedService.interaction_type || this.newMeeting.interaction_type
      const account = this.account
      const isCustom = this.isSelectedServiceCustom;
      const settingsSourceObject = isCustom ? account.business : selectedService
      const serviceSpecificFields = {
        padding: isCustom ? 0 : selectedService.padding,
        name: isCustom ? '' : selectedService.name,
        charge_type: isCustom ? 'free' : selectedService.charge_type,
        display: isCustom ? false : selectedService.display,
        invitation_only: isCustom ? true : !selectedService.display,
        price: isCustom ? 0 : selectedService.price,
        currency: isCustom ? account.settings.currency : selectedService.currency,
        approval_mode: isCustom ? account.settings.approval_mode : selectedService.approval_mode,
        max_attendance: isCustom ? this.customMaxAttendance : selectedService.max_attendance,
        min_hours_before_meeting: settingsSourceObject.min_hours_before_meeting,
        future_bookings_limit: settingsSourceObject.future_bookings_limit,
        remind_before_in_hours_1: settingsSourceObject.remind_before_in_hours_1,
        remind_before_in_hours_2: settingsSourceObject.remind_before_in_hours_2,
        meeting_reminder_sms_1: settingsSourceObject.meeting_reminder_sms_1,
        meeting_reminder_sms_2: settingsSourceObject.meeting_reminder_sms_2,
        sms_booking_confirmation: settingsSourceObject.sms_booking_confirmation,
      }
      //In case of a duplicate a custom event
      this.getSpecificDuplicateFields(serviceSpecificFields)

      this.newMeeting = {...this.newMeeting, ...serviceSpecificFields}
    },
    getDefaultBooking() {
      return {
        padding: 0,
        max_attendance: '',
        type: 'event'
      };
    },
    focusOnFirstElementWithError() {
      if (this.selectedService && ((this.isPaidMeeting && !this.newMeeting.price) || !this.newMeeting.interaction_details)) {
        this.$refs.interactionDetailsPanel.expandAll()
      }
      const elementWithError = this.$refs.eventForm.inputs.find(input => !input.valid)
      elementWithError.$el.scrollIntoView()
      const inputElement = elementWithError.$el.querySelector('input')
      if (inputElement) {
        setTimeout(() => inputElement.focus({preventScroll: false}))
      } else {
        setTimeout(() => elementWithError.focus())
      }
    },
    async handleSubmit() {
      if (this.isSubmitting) {
        return
      }
      const isValid = this.$refs.eventForm.validate()

      if (!isValid) {
        this.focusOnFirstElementWithError()
        return;
      }

      if (this.shouldDisplayZoomWarning) {
        this.$refs.locationSelector.$el.scrollIntoView()
        setTimeout(() => this.$refs.locationSelector.$el.focus({preventScroll: false}))
        return;
      }

      let res;
      try {
        let callParams = this.callParams
        this.isSubmitting = true
        res = await createEvent(callParams)
      } catch (e) {
        console.error(e)
        const serverErrors = e && e.errors && e.errors.errors
        if (serverErrors) {
          this.serverErrors = serverErrors
        }
        this.isSubmitting = false
        return
      }
      this.isSubmitting = false
      const newEvent = res.data
      if (new Date(newEvent.start_time) > new Date()) {
        showSuccess(this.$t("event.action.new.success.instance"), {})
      }
      this.$track('New Event - Success', {new_dialog: true})
      this.$emit('submit', newEvent)
    },
    checkServicesMap() {
      let that = this
      Object.keys(this.servicesMap).filter(serviceUidKey => {
        if (serviceUidKey === 'undefined') {
          that.servicesMap[that.servicesMap[serviceUidKey].uid] = that.servicesMap[serviceUidKey]
        }
      })
    }
  },
  computed: {
    callParams() {
      let start_time = null
      let end_time = null
      if (this.isStartTimeValid) {
        start_time = createDateAsUTC(this.startTimeDateObject).toISOString()
      }

      if (!isNaN(this.endTimeDateObject.getTime())) {
        let endTimeDateObject = this.endTimeDateObject
        if (this.shouldDisplayEndFollowingDayWarning) {
          endTimeDateObject = new Date(endTimeDateObject)
          endTimeDateObject.setDate(endTimeDateObject.getDate() + 1)
        }
        end_time = createDateAsUTC(endTimeDateObject).toISOString()
      }

      const selectedService = this.selectedService
      const newEvent = this.newMeeting
      return {
        title: this.isSelectedServiceCustom ? this.customTitle : newEvent.name,
        start_time: start_time,
        end_time: end_time,
        padding: newEvent.padding || 0,
        min_hours_before_meeting: newEvent.min_hours_before_meeting,
        future_bookings_limit: newEvent.future_bookings_limit,
        remind_before_in_hours_1: newEvent.remind_before_in_hours_1,
        remind_before_in_hours_2: newEvent.remind_before_in_hours_2,
        max_attendance: newEvent.max_attendance,
        display: !newEvent.invitation_only,
        meeting_reminder_sms_1: newEvent.meeting_reminder_sms_1,
        meeting_reminder_sms_2: newEvent.meeting_reminder_sms_2,
        sms_booking_confirmation: newEvent.sms_booking_confirmation,
        interaction_type: newEvent.interaction_type,
        interaction_details: newEvent.interaction_details,
        charge_type: newEvent.charge_type,
        price: this.isPaidMeeting ? newEvent.price : null,
        currency: newEvent.currency,
        agenda: newEvent.notes,
        require_confirmation: newEvent.require_confirmation,
        event_service_id: selectedService.id !== 'custom' ? selectedService.id : null,
        staff_id: this.selectedStaff.uid,
        mobile: this.isMobile,
        source_type: 'initiated_by_staff',
        ...this.formattedRecurrenceParams,
      }
    },
    modifiedCategories() {
      if (!this.categories.length) {
        return []
      }
      let res = this.categories.map(category => {
        let newCategory = {...category}
        newCategory.services = newCategory.services.filter(service => service.type === "EventService" || service.service_type === "event")
        newCategory.num_of_services = newCategory.services.length
        return newCategory
      })
      if (this.recentlyPickedServices && this.recentlyPickedServices.length && this.categories.length) {
        const categoryToAdd = {
          id: 'recentlyAdded',
          name: this.$t('booking.info.recently_picked'),
          num_of_services: this.recentlyPickedServices.length,
          position: 0,
          services: this.recentlyPickedServices.map(service => this.servicesMap[service.event_service_uid]),
          uid: '',
        }
        res.unshift(categoryToAdd)
      }
      return res.filter(category => category.services.length > 0)
    },
    priceRules() {
      return [
        val => {
          if (this.isPaidMeeting) {
            return !!val || this.$t('booking.validation_errors.required_input')
          } else {
            return true
          }
        }
      ]
    },
    maxAttendanceRules() {
      return [
        val => {
          return val > 0 || ''
        }]
    },
    preparationTimes() {
      let times = [];
      for (let i = 0; i <= 12; i++) {
        times.push({
          text: i * 5 + ' ' + this.$t('common.minutes'),
          value: i * 5
        });
      }
      return times
    },
    getDisabled() {
      if (this.isSelectedServiceCustom) {
        return 'rgba(0, 0, 0, 0.07);';
      }
      return '#54d28c';
    }
  },
}

</script>

<style lang="scss" scoped>
@import '@/assets/scss/abstract/_i18n-mixins';
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.event-dialog-container {
  @include hide-scrollbar;
  overflow-x: hidden;
  margin: auto;
  height: auto;
  max-height: 100%;
  min-height: 100%;
  border-radius: 0;
  @include md-and-up {
    max-width: 500px;
    max-height: 93vh;
    height: revert;
    min-height: revert;
    border-radius: 10px;
  }

  .recurring-link {
    padding: 12px 0;
    cursor: pointer;
    font-style: normal !important;
    font-weight: 500 !important;
    font-size: 15px !important;
    color: #478FFF !important;
    @include md-and-up {
      padding: 0;
      font-size: 13px !important;
    }

    i {
      color: #478FFF;
    }

    .text {
      text-decoration-line: underline;
    }
  }

  .dialog-expansion-panel {
    margin-bottom: 16px;

    ::v-deep {
      .default-header {
        width: unset;
      }
    }
  }

  .dialog-text-area {
    overflow: hidden;
    max-height: 1000px;
    transition: max-height 0.3s;

    font-family: var(--primary-font-family);
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 24px;
    letter-spacing: -0.2px;
    color: rgba(0, 0, 0, 0.4);

    ::v-deep {
      .v-input__slot {
        height: 104px;
        box-shadow: none !important;
        border: 1px solid rgba(0, 0, 0, 0.12);
      }

      textarea {
        line-height: 24px;
      }
    }
  }

  ::v-deep {
    .v-expansion-panel--active {
      @include md-and-up {
        margin-bottom: 0;
      }
    }

    .v-expansion-panel--disabled {
      .v-expansion-panel-header__icon {
        display: revert !important;
      }

      button {
        font-size: 14px;
        font-weight: 600;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.43;
        letter-spacing: normal;
        color: rgba(0, 0, 0, 0.4);

        i {
          color: rgba(0, 0, 0, 0.4) !important;
        }
      }
    }

    .v-expansion-panel-header {
      padding: 0 !important;
    }

    .v-expansion-panel-header__icon {
      order: 0;
    }

    .panel-title {
      order: 1;
      margin: 0 8px;
    }

    .v-expansion-panel {
      padding: 0;

      &:before {
        box-shadow: none;
      }

      .v-expansion-panel-content__wrap {
        padding: 0;
      }

      .v-expansion-panel-header {
        .v-icon {
          position: initial;
          top: unset;
          @include i-position-mirror(left, unset);
        }
      }
    }
  }

  .custom-title-input {
    overflow: hidden;
  }

  .hide {
    max-height: 0;
  }
}

.dialog-form {
  max-width: 100%;
  min-width: 100%;
  @include hide-scrollbar;
  @include md-and-up {
    max-height: 743px;
    overflow-y: auto;
  }
}

.switch-button {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
  text-decoration-line: underline;
  @include i-margin-mirror(0, 10px);
}

.event-details-section {

  .service-select {
    flex: 0;
    margin-bottom: 20px;
  }

  .spacing-gap {
    .one-flex-grow {
      flex: 1;
    }

    &:not(.column) > div:not(:last-child) {
      @include i-margin-mirror(0, 16px);
    }

    &.column {
      & > div:not(:last-child) {
        margin-bottom: 16px;
      }
    }
  }
}

.input-section {
  flex: 0;
  padding: 16px;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.03);

  & > div:not(:last-child) {
    margin-bottom: 16px;
  }

  .wide ::v-deep {
    .v-input__control {
      .v-label {
        max-width: 100%;
      }
    }
  }
}

.warning-text {
  font-family: var(--primary-font-family);
  align-self: center;
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.2px;
}

.left-side-container {
  & > div:last-child {
    margin-bottom: 120px;
    @include md-and-up {
      margin-bottom: 0;
    }
  }
}

.warning-icon {
  @include i-padding-mirror(0, 14px);
  max-height: 20px;
}

.dialog-checkbox {
  text-align: initial;
  max-height: 300px;
  transition: max-height 0.3s;
  overflow: hidden;

  &.hide {
    margin-top: -16px;
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
}

.input-section-title {
  font-family: var(--primary-font-family);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.33;
  letter-spacing: -0.2px;
  margin-bottom: 16px;
  white-space: nowrap;
  @include md-and-up {
    margin-bottom: 8px;
  }
}

.event-details-title {
  @include md-and-up {
    margin-inline-end: 8px;
  }
}

.matter-term-title {
  margin-bottom: 16px;
}

.dialog-separator {
  width: 100%;
  min-height: 1px;
  background-color: rgba(0, 0, 0, 0.12);
  min-width: calc(100% + 50px);
  margin-left: -25px;
}

.service-separator {
  margin-bottom: 16px;
}
</style>

<style lang="scss">
@import '@/assets/scss/abstract/_i18n-mixins';
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.event-dialog-container {

  .country-code {
    .v-select__slot {
      .v-select__selections {
        .vti__flag {
          @include i-margin-mirror(20px, 0)
        }
      }
    }
  }
}

.tooltip {
  padding: 8px;
  max-width: 224px;
  background: rgba(0, 0, 0, 0.62);
  border-radius: 4px;

  font-family: var(--primary-font-family);
  letter-spacing: -0.3px;
  font-size: 13px;
  line-height: 16px;
}
</style>



